import AppBrowser from './appBrowserUtil'
import Analytics from './analyticsUtil'
import Dialog from './dialogUtil'
import isEmpty from './isEmpty'
import Logger from './loggerUtil'

import { AnalyticsConstants, ExternalAppConstants, RegexConstants } from '../constants'

class ExternalAppUtil {
  static async canOpenApp (appIdentifier) {
    if (!window.hasOwnProperty('cordova')) {
      return false
    }

    if (isEmpty(appIdentifier)) {
      return false
    }

    Logger.debug('Checking if external app is available: ' + appIdentifier)

    if (appIdentifier === ExternalAppConstants.scheme.appleMaps) {
      if (window.device.platform.toLowerCase() === 'ios') { // Apple Maps scheme is just http so its always available
        Logger.debug('Apple Maps app is available')
        return true
      }

      Logger.debug('Apple Maps app is not available')
      return false // Apple Maps is not installed on other devices
    }

    return new Promise((resolve, reject) => {
      window.appAvailability.check(appIdentifier, function () {
        Logger.debug('External app is available')
        resolve(true)
      }, function () {
        Logger.debug('External app is not available')
        resolve(false)
      })
    })
  }

  static async openMaps (destinationAddress) {
    const encodedUri = encodeURI(destinationAddress)

    const appleResult = await ExternalAppUtil.canOpenApp(ExternalAppConstants.scheme.appleMaps())
    const googleResult = await ExternalAppUtil.canOpenApp(ExternalAppConstants.scheme.googleMaps())

    if (googleResult === true && appleResult === true) {
      const buttonIndex = await Dialog.confirm('')

      switch (buttonIndex) {
        case 1:
          AppBrowser.openInBrowser(ExternalAppConstants.url.appleMapsApp() + encodedUri, '_system')
          break

        case 2:
          AppBrowser.openInBrowser(ExternalAppConstants.url.googleMapsApp() + encodedUri, '_system')
          break

        case 3:
          break
      }
    } else if (googleResult === false && appleResult === false) {
      AppBrowser.openInBrowser('https://www.google.com/maps/dir/?api=1&destination=' + encodedUri + '&language=' + window.xlang, '_system')
    } else {
      if (appleResult === true) {
        AppBrowser.openInBrowser(ExternalAppConstants.url.appleMapsApp() + encodedUri, '_system')
      } else if (googleResult === true) {
        AppBrowser.openInBrowser(ExternalAppConstants.url.googleMapsApp() + encodedUri, '_system')
      }
    }
  }

  static phoneCall (phone, eventSource) {
    const formattedPhone = phone.replace(RegexConstants.phoneNumberFormatting, '')

    Logger.debug(`Formatted Phone: ${formattedPhone}`)
    Analytics.logEvent(AnalyticsConstants.eventName.selectContent, { content_type: AnalyticsConstants.itemType.phone, value: formattedPhone, origin: eventSource }, { logMarketingEvent: true })

    document.location.href = `tel:${formattedPhone}`
  }

  static email (emailAddress, eventSource) {
    Logger.debug(`Email Address: ${emailAddress}`)
    Analytics.logEvent(AnalyticsConstants.eventName.selectContent, { content_type: AnalyticsConstants.itemType.email, value: emailAddress, origin: eventSource }, { logMarketingEvent: true })

    document.location.href = `mailto:${emailAddress}`
  }

  /**
   * Opens a PDF URL in an external browser to allow sharing features.
   * @param {String} url A valid PDF URL.
   * @param {String} [filename] The name of the PDF.
   * @param {String} eventSource The source of the event for analytics.
   */
  static pdfUrl (url, filename, eventSource) {
    Logger.debug('PDF Name: ' + filename)

    Analytics.logEvent(AnalyticsConstants.eventName.selectContent, { content_type: AnalyticsConstants.itemType.pdf, value: filename, origin: eventSource }, { logMarketingEvent: true })

    return AppBrowser.openInBrowser(url, '_system')
  }

  /**
   * Opens Chrome Tabs (Android) or SFSafariViewController (iOS) using the passed in URL. Falls back to external browser otherwise.
   * @param {String} url The URL to visit.
   */
  static async browserTabs (url) {
    if (!window.hasOwnProperty('cordova')) {
      Logger.info('Falling back to external browser')
      AppBrowser.openInBrowser(url, '_system')
      return
    }

    let canOpen = false

    try {
      canOpen = await new Promise((resolve, reject) => window.cordova.plugins.browsertab.isAvailable(resolve, reject))
    } catch (e) {
      Logger.error('Error checking for browser tabs')
      Logger.debug(e)
    }

    Logger.debug(`Can open browser tabs: ${canOpen}`)

    if (!canOpen) {
      Logger.info('Falling back to external browser')
      AppBrowser.openInBrowser(url, '_system')
      return
    }

    try {
      await new Promise((resolve, reject) => window.cordova.plugins.browsertab.openUrl(url, resolve, reject))
    } catch (e) {
      Logger.error('Error checking for browser tabs')
      Logger.debug(e)

      AppBrowser.openInBrowser(url, '_system')
    }
  }
}

export default ExternalAppUtil
